import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Box from 'fe-design-base/atoms/Box';
import Select from 'fe-design-base/atoms/Select';
import AlertText from 'fe-design-base/molecules/AlertText';
import { partial } from 'lodash';
import PropTypes from 'prop-types';

import { DATE_TIME_FORMAT } from 'features/timesheets/constants.js';
import {
  duration,
  overnightShiftDateAdjustment,
  updateMomentDate,
} from 'features/timesheets/util';

import TimePicker from 'components/forms/TimePicker';

import { cxHelpers } from 'util/className';
import { moment } from 'util/dateTime';
import { toI18n } from 'util/i18n';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
} from 'util/tracking_constants';

import BreakDeleteButton from './BreakDeleteButton';

const { cx, cxEl } = cxHelpers('IndividualBreak');

import Checkbox from 'fe-design-base/atoms/Checkbox';
import Text from 'fe-design-base/atoms/Text';

import {
  TIME_FORMAT,
  TIME_FORMAT_MOBILE,
} from 'features/timesheets/TimesheetsPage/TimecardModal/constants';
import {
  TIMECARD_SELECT_INPUTS,
  trackDropDownDeleteClicked,
  trackDropDownInputSelected,
  trackDropDownMenuClick,
} from 'features/timesheets/tracking';

import { trackUxEvent } from 'util/tracking';

const IndividualBreak = ({
  dateStr,
  endAt,
  isMobileScreenSize,
  mandatedBreakId,
  mandatedBreaks,
  onChangeTimebreak,
  removeTimebreak,
  shiftEndAt,
  shiftStartAt,
  startAt,
  timebreakId,
  validationErrors,
  remove,
  paidTimebreak,
  isWaived,
  employeeName,
  setChangesMade,
}) => {
  const trackingTimebreakType = paidTimebreak
    ? TIMECARD_SELECT_INPUTS.PAID_BREAK_TYPE
    : TIMECARD_SELECT_INPUTS.UNPAID_BREAK_TYPE;

  const waived_flag = 'employee_waived';

  const options = useMemo(
    () =>
      mandatedBreaks
        .filter(
          mb =>
            mb.get('active') ||
            (mb.get('id') === mandatedBreakId && !isNaN(timebreakId))
        )
        .sortBy(mb => mb.get('duration'))
        .map(mb => ({
          value: mb.get('id'),
          label: duration(mb.get('duration')),
        })),
    [mandatedBreaks, mandatedBreakId, timebreakId]
  );

  const getOptionFromMandatedBreakId = useCallback(
    breakId => {
      const option = options.find(o => o.value === breakId);
      return option;
    },
    [options]
  );
  const [durationValue, setDurationValue] = useState(
    getOptionFromMandatedBreakId(mandatedBreakId)?.value
  );

  const handleBreakDurationChange = useCallback(
    e => {
      setDurationValue(e.target.value);
      onChangeTimebreak('mandated_break_id', e.target.value);
    },
    [onChangeTimebreak]
  );

  const handleClickRemoveTimebreak = useCallback(() => {
    trackUxEvent({
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction: EVENT_ACTIONS.DELETE_TIMECARD_BREAK_CLICKED_V3,
    });
    removeTimebreak();
    setChangesMade(true);
  }, [removeTimebreak, setChangesMade]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const showWaivedSection = useMemo(() => isWaived, []); // store the initial value of this variable
  const [markedForUnwaive, setMarkedForUnwaive] = useState(false);
  const breakDuration = useMemo(() => {
    // return selected mandated break's duration
    // or shortest active break if none is selected
    const selectedBreak =
      mandatedBreakId &&
      mandatedBreaks.find(mb => mb.get('id') === mandatedBreakId);

    const shortestBreakDuration = () => {
      try {
        mandatedBreaks
          .filter(mb => mb.get('active'))
          .sortBy(mb => mb.get('duration'))
          .first()
          .get('duration');
      } catch {
        mandatedBreaks
          .sortBy(mb => mb.get('duration'))
          .first()
          .get('duration');
      }
    };

    return selectedBreak
      ? selectedBreak.get('duration')
      : shortestBreakDuration;
  }, [mandatedBreaks, mandatedBreakId]);

  useEffect(
    () => {
      let startAtTime = startAt;
      let endAtTime = endAt;

      if (
        shiftStartAt &&
        shiftEndAt &&
        ((startAt && !startAt.isBetween(shiftStartAt, shiftEndAt)) ||
          (endAt && !endAt.isBetween(shiftStartAt, shiftEndAt)))
      ) {
        startAtTime = updateMomentDate(startAt, shiftStartAt);
        endAtTime = updateMomentDate(endAt, shiftStartAt);

        const newStartTime = overnightShiftDateAdjustment(
          shiftStartAt,
          startAtTime?.format(DATE_TIME_FORMAT)
        );
        const newEndTime = overnightShiftDateAdjustment(
          shiftStartAt,
          endAtTime?.format(DATE_TIME_FORMAT)
        );

        onChangeTimebreak('start_at', newStartTime);
        onChangeTimebreak('end_at', newEndTime);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftStartAt, shiftEndAt]
  );

  const handleTimeChange = useCallback(
    ({ target: { name, value } }) => {
      const valueDateTime = overnightShiftDateAdjustment(
        shiftStartAt,
        moment(`${dateStr} ${value}`, 'MM-DD-YYYY HH:mm').format(
          DATE_TIME_FORMAT
        )
      );
      onChangeTimebreak(name, value ? valueDateTime : null);
      setChangesMade(true);
    },
    [dateStr, onChangeTimebreak, setChangesMade, shiftStartAt]
  );

  const dropDownTrackingProp = paidTimebreak
    ? {
        start_at: TIMECARD_SELECT_INPUTS.PAID_BREAK_START,
        end_at: TIMECARD_SELECT_INPUTS.PAID_BREAK_END,
      }
    : {
        start_at: TIMECARD_SELECT_INPUTS.UNPAID_BREAK_START,
        end_at: TIMECARD_SELECT_INPUTS.UNPAID_BREAK_END,
      };

  const handleClear = useCallback(
    timeType => {
      trackDropDownDeleteClicked({
        properties: { drop_down: dropDownTrackingProp[timeType] },
      });
      setChangesMade(true);
    },
    [dropDownTrackingProp, setChangesMade]
  );

  const handleSelect = useCallback(
    timeType => {
      trackDropDownInputSelected({
        properties: { drop_down: dropDownTrackingProp[timeType] },
      });
    },
    [dropDownTrackingProp]
  );

  const handleMobileTimeChange = useCallback(
    (name, e) => {
      handleTimeChange({ target: { name, value: e.target.value } });
    },
    [handleTimeChange]
  );

  const timebreakStartAt = useMemo(
    () => (!!startAt ? moment(startAt) : moment()),
    [startAt]
  );

  const handleChangeIsWaived = useCallback(
    e => {
      trackUxEvent({
        productArea: PRODUCT_AREAS.BREAK_ENFORCEMENT,
        eventCategory: EVENT_CATEGORIES.ACKNOWLEDGED_WAIVED_BREAK,
        eventAction: EVENT_ACTIONS.CHECKBOX_CLICKED,
        properties: {
          value: e.target.checked,
        },
      });

      setMarkedForUnwaive(!e.target.checked);

      onChangeTimebreak(waived_flag, e.target.checked);
      setChangesMade(true);
    },
    [onChangeTimebreak, setChangesMade]
  );

  const timebreakEndAt = useMemo(() => {
    if (!!endAt) {
      return moment(endAt);
    }
    return !!startAt ? moment(startAt).add(breakDuration, 'minutes') : null;
  }, [startAt, endAt, breakDuration]);

  const trackOpenType = useCallback(() => {
    trackDropDownMenuClick({
      properties: { drop_down: trackingTimebreakType },
    });
  }, [trackingTimebreakType]);

  const trackOpenTime = useCallback(
    timeType => {
      trackDropDownMenuClick({
        properties: { drop_down: dropDownTrackingProp[timeType] },
      });
    },
    [dropDownTrackingProp]
  );

  if (remove) {
    return null;
  }

  return (
    <Box className={cx()} column mbxxs>
      {showWaivedSection && (
        <Box row gap={12} pb={4} pt={8}>
          <Box>
            <Checkbox
              checked={isWaived}
              name="employee_waived"
              onChange={handleChangeIsWaived}
            />
          </Box>
          <Box className={cxEl('label')} vcenter>
            <Text variant="bodySm" inline>
              {employeeName}
            </Text>
            &nbsp;
            <Text
              inline
              variant="bodySm"
              i18n="timecard_modal_v3.issues.missing_breaks.employee_waived_label"
            />
          </Box>
        </Box>
      )}
      <Box pb={8} row>
        <Box spaceevenly>
          <Box w={isMobileScreenSize ? 89 : 112} onClick={trackOpenType}>
            <Select
              name="mandated_break_id"
              disabled={isWaived}
              options={options}
              value={durationValue}
              onChange={handleBreakDurationChange}
            />
          </Box>
        </Box>
        <Box flex grow={1} justifyContentSpaceBetween>
          {isMobile ? (
            <Box row vcenter justifySpaceEvenly pl={8}>
              <Box w={82} className={cxEl('start-at')}>
                <input
                  type="time"
                  name="start_at"
                  style={{
                    fontSize: '14px',
                    padding: '3px',
                    lineHeight: '40px',
                  }}
                  value={startAt && startAt.format(TIME_FORMAT_MOBILE)}
                  disabled={isWaived}
                  onChange={partial(handleMobileTimeChange, 'start_at')}
                  autoComplete="false"
                />
              </Box>
              <Box mh={8}>-</Box>
              <Box w={82} className={cxEl('end-at')}>
                <input
                  type="time"
                  name="end_at"
                  style={{
                    fontSize: '14px',
                    padding: '3px',
                    lineHeight: '40px',
                  }}
                  value={endAt && endAt.format(TIME_FORMAT_MOBILE)}
                  disabled={isWaived}
                  onChange={partial(handleMobileTimeChange, 'end_at')}
                  autoComplete="false"
                />
              </Box>
              <Box>
                <BreakDeleteButton
                  disabled={!markedForUnwaive && showWaivedSection}
                  onClick={handleClickRemoveTimebreak}
                />
              </Box>
            </Box>
          ) : (
            <Box column grow={1}>
              <Box row vcenter flex grow={1}>
                <Box
                  flex
                  grow={1}
                  mh={4}
                  className={cxEl('start-at')}
                  onClick={partial(trackOpenTime, 'start_at')}
                >
                  <TimePicker
                    className={cxEl('TimePicker')}
                    name="start_at"
                    disabled={isWaived}
                    validationError={validationErrors.length > 0}
                    value={startAt ? startAt.format(TIME_FORMAT) : null}
                    onChange={handleTimeChange}
                    onClear={partial(handleClear, 'start_at')}
                    onSelect={partial(handleSelect, 'start_at')}
                    overrideOnFocus={!startAt}
                    timeToOverride={timebreakStartAt.format(TIME_FORMAT)}
                    iconSize="xsmall"
                    endIconName="Close"
                  />
                </Box>
                <Box>-</Box>
                <Box
                  mh={4}
                  className={cxEl('end-at')}
                  onClick={partial(trackOpenTime, 'end_at')}
                >
                  <TimePicker
                    className={cxEl('TimePicker')}
                    name="end_at"
                    disabled={isWaived}
                    validationError={validationErrors.length > 0}
                    value={endAt ? endAt.format(TIME_FORMAT) : null}
                    onChange={handleTimeChange}
                    onClear={partial(handleClear, 'end_at')}
                    onSelect={partial(handleSelect, 'end_at')}
                    overrideOnFocus={!!startAt && !endAt}
                    timeToOverride={
                      timebreakEndAt && timebreakEndAt.format(TIME_FORMAT)
                    }
                    iconSize="xsmall"
                    endIconName="Close"
                  />
                </Box>
                <Box>
                  <BreakDeleteButton
                    disabled={!markedForUnwaive && showWaivedSection}
                    onClick={handleClickRemoveTimebreak}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {showWaivedSection && markedForUnwaive && (
        <Box pb={8}>
          <AlertText
            variant="warning"
            message={toI18n('timecard_modal_v3.issues.missing_breaks.warning')}
          />
        </Box>
      )}
      {validationErrors.length > 0 && (
        <Box mb={8} className={cxEl('validation-error')}>
          <AlertText variant="error" message={validationErrors.join(', ')} />
        </Box>
      )}
    </Box>
  );
};

IndividualBreak.propTypes = {
  dateStr: PropTypes.string,
  endAt: PropTypes.object,
  isMobileScreenSize: PropTypes.bool,
  mandatedBreakId: PropTypes.number,
  mandatedBreaks: ImmutablePropTypes.list,
  onChangeTimebreak: PropTypes.func.isRequired,
  removeTimebreak: PropTypes.func.isRequired,
  shiftEndAt: PropTypes.object,
  shiftStartAt: PropTypes.object,
  startAt: PropTypes.object,
  timebreakId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  validationErrors: PropTypes.array,
  remove: PropTypes.bool,
  paidTimebreak: PropTypes.bool,
  isWaived: PropTypes.bool,
  employeeName: PropTypes.string,
  setChangesMade: PropTypes.func.isRequired,
};

export default IndividualBreak;
